import { useCallback, useEffect, useRef, useState } from 'react';
import { isAdmin } from '../services/api';
import { getSamplingBoost } from '../services/samplingBoost';
import { formatBoostShort } from '../utils/ruleSamplingBoost';

/**
 * Sampling Boost в таблице правил (ТЗ №39, только просмотр). Ответ списка
 * правил про ускорение ничего не содержит, а GET есть только на одно правило,
 * поэтому грузим его для правил текущей страницы, только администратору.
 *
 * Очередь на 4 параллельных запроса, кэш по rule_id переживает пагинацию
 * назад-вперёд (повторно не запрашиваем). Запущенные запросы не отменяются, из
 * очереди снимаются только ещё не стартовавшие правила ушедшей страницы:
 * «загружается» без живого запроса или очереди не бывает.
 * Упавший запрос остаётся прочерком и никогда не блокирует таблицу.
 */
const CONCURRENCY = 4;

const newGeneration = () => ({ cache: new Map(), queue: [], active: 0 });

export const useRuleBoosts = (ruleIds, { refreshToken = 0 } = {}) => {
  const genRef = useRef(newGeneration());
  const tokenRef = useRef(refreshToken);
  const mountedRef = useRef(true);
  const [, forceRender] = useState(0);
  const key = (Array.isArray(ruleIds) ? ruleIds : []).join(',');

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (tokenRef.current !== refreshToken) {
      // Новое поколение: ответы старого уже не пишутся в актуальный кэш.
      tokenRef.current = refreshToken;
      // Очередь старого поколения больше не нужна — не догружаем её.
      genRef.current.queue = [];
      genRef.current = newGeneration();
      forceRender((n) => n + 1);
    }
    if (!isAdmin()) return;

    const gen = genRef.current;
    const ids = key ? key.split(',') : [];
    const visible = new Set(ids);

    // Не стартовавшие запросы ушедшей страницы снимаем: у них нет владельца.
    gen.queue = gen.queue.filter((id) => {
      if (visible.has(id)) return true;
      gen.cache.delete(id);
      return false;
    });

    const pending = ids.filter((id) => id && !gen.cache.has(id));
    if (pending.length === 0) return;
    pending.forEach((id) => { gen.cache.set(id, { state: 'loading' }); gen.queue.push(id); });
    forceRender((n) => n + 1);

    const pump = () => {
      while (gen.active < CONCURRENCY && gen.queue.length > 0) {
        const id = gen.queue.shift();
        gen.active += 1;
        getSamplingBoost(id)
          .then((config) => { gen.cache.set(id, { state: 'ready', config }); })
          .catch(() => { gen.cache.set(id, { state: 'error' }); })
          .then(() => {
            gen.active -= 1;
            if (mountedRef.current && gen === genRef.current) forceRender((n) => n + 1);
            pump();
          });
      }
    };
    pump();
  }, [key, refreshToken]);

  const boostLabel = useCallback((ruleId) => {
    const entry = genRef.current.cache.get(ruleId);
    if (entry?.state === 'loading') return '…';
    if (entry?.state !== 'ready') return '—';
    return formatBoostShort(entry.config);
  }, []);

  return { boostLabel };
};

export default useRuleBoosts;
